import classNames from "classnames";
import { Bookmark, Check, Plus } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router";

import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { formatDate } from "@/shared/datetime";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";
import { useViews } from "@/shared/domain/views/hooks";
import {
  ConfirmDialog,
  Popover,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";

import type { FilterDefinition } from "../Filters";

import { partitionViewsByVisibility } from "./partitionViewsByVisibility";
import { resetRowCopy } from "./resetRowCopy";
import { useDefaultView } from "./useDefaultView";
import { useDefaultViewArrival } from "./useDefaultViewArrival";
import { useManageViews, type ManageViews } from "./useManageViews";
import { useViewDeepLink, VIEW_SEARCH_PARAM } from "./useViewDeepLink";
import styles from "./ViewPicker.module.css";
import { ViewRowMenu } from "./ViewRowMenu";

type PickerTab = "personal" | "team";

export interface ViewPickerProps {
  /** Resource type whose Views to offer. A View is written against one target. */
  target: QueryTarget;
  /** The View currently applied, or `null` when the table is showing ad-hoc filters. */
  loadedView: ViewRecord | null;
  /**
   * Apply a View. The caller restores filters, sort, paging and columns from it,
   * and reports whether the apply took — the deep link needs that to know when
   * to stop advertising a View that never loaded.
   */
  onSelectView: (view: ViewRecord) => Promise<boolean>;
  /**
   * A View the route loader resolved and the list was already built from, so
   * the deep link has nothing left to resolve for it. From
   * `useLoadedView().seededViewId`.
   */
  seededViewId?: string;
  /** Unload the current View and return the table to its unfiltered default. */
  onClearView: () => void;
  /** Absolute URL that reopens a View, for the row's Copy link action. */
  linkFor: (view: ViewRecord) => string;
  /**
   * The table's fields. The empty state offers a shortcut that opens the filter
   * menu on the first of them. Omit to render the empty state as text alone.
   */
  filterDefinitions?: FilterDefinition[];
  /**
   * Start a filter on `field` — the caller opens its filter menu on it. The
   * picker closes itself first; the two panels must not be up at once.
   */
  onStartFilter?: (field: FilterDefinition) => void;
}

/**
 * Browse the Views saved on a resource type and apply one.
 *
 * Lives in the toolbar rather than the nav: a View is scoped to one target, so
 * it belongs beside the table it applies to. This only *chooses* a View —
 * turning one into filter state is the caller's job.
 */
export function ViewPicker(props: ViewPickerProps) {
  const viewsEnabled = useCheckFeatureFlag("release-views");

  // Every View endpoint 403s until `release-views` is on. A wrapper rather than
  // an early return inside the panel: the panel calls `useViews`, and not
  // mounting it is what keeps the request from being made.
  if (!viewsEnabled) {
    return null;
  }

  return (
    <>
      <ViewPickerPanel {...props} />
      <div className={styles.toolbarSeparator} aria-hidden />
    </>
  );
}

function ViewPickerPanel({
  target,
  loadedView,
  onSelectView,
  seededViewId,
  onClearView,
  linkFor,
  filterDefinitions,
  onStartFilter,
}: ViewPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<PickerTab>("personal");
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  // Held as a record so the dialog can still name it after the list refetches.
  const [pendingDelete, setPendingDelete] = React.useState<ViewRecord | null>(
    null,
  );
  const { data: views, isPending } = useViews(target);
  const manage = useManageViews();
  const defaultView = useDefaultView(target);

  // Destructured rather than closing over `defaultView`: the hook returns a
  // fresh object each render, so the handler would change identity every time.
  const { isDefault, setDefault, clearDefault } = defaultView;
  const [defaultError, setDefaultError] = React.useState<string | null>(null);
  const handleToggleDefault = React.useCallback(
    (view: ViewRecord) => {
      setDefaultError(null);
      // The star is driven by the preferences query, which the mutation
      // invalidates, so the mark follows the server rather than an optimistic
      // guess a failed write would strand. That makes a failure invisible
      // unless it is reported: the row simply looks the way it did before the
      // click, which reads as the menu item not working.
      const saving = isDefault(view) ? clearDefault() : setDefault(view);
      saving.catch(() => {
        setDefaultError("Couldn't save your default View. Please try again.");
      });
    },
    [isDefault, setDefault, clearDefault],
  );

  // Resolved here rather than in the list: this reaches for `ViewService`.
  const { linkError } = useViewDeepLink({
    loadedView,
    onResolved: onSelectView,
    seededViewId,
  });

  // Read once, at mount. An applied default writes `?view=` itself, so reading
  // this live would let the default see a link it had just created and stand
  // down against itself on a later render.
  const [searchParams] = useSearchParams();
  const [deepLinkPresent] = React.useState(() =>
    searchParams.has(VIEW_SEARCH_PARAM),
  );

  // Position 3 of the arrival order, behind the deep link above. Same
  // `onSelectView`, so a default arrives through the path a chosen View does.
  useDefaultViewArrival({
    target,
    deepLinkPresent,
    onResolved: onSelectView,
  });

  const { personal, team, unknown } = React.useMemo(
    () => partitionViewsByVisibility(views ?? []),
    [views],
  );

  // Unreported visibility shows under Team: calling a shared View personal is
  // the worse of the two mistakes.
  const teamViews = React.useMemo(() => [...team, ...unknown], [team, unknown]);
  const shown = tab === "personal" ? personal : teamViews;

  const resetCopy = resetRowCopy(target);

  const viewCount = views?.length ?? 0;

  // Choosing dismisses the panel, which otherwise covers the table it changed.
  const handleSelect = React.useCallback(
    (view: ViewRecord) => {
      setOpen(false);
      // The outcome matters to the deep link, which calls `onSelectView`
      // itself and awaits it; a click has nothing to do with it either way.
      void onSelectView(view);
    },
    [onSelectView],
  );

  const handleClear = React.useCallback(() => {
    setOpen(false);
    onClearView();
  }, [onClearView]);

  // Deleting the loaded View takes its filters with it, or the delete reads as
  // though it silently failed.
  // The dialog stays up until the delete settles: closing it first would
  // leave the row in place with nothing to say it was going.
  const handleConfirmDelete = React.useCallback(
    async (view: ViewRecord) => {
      await manage.deleteView(view);
      setPendingDelete(null);
      if (loadedView?.view_id === view.view_id) {
        onClearView();
      }
    },
    [loadedView, manage, onClearView],
  );

  // Closes the panel first: the filter menu opens against the same toolbar.
  const handleStartFilter = React.useCallback(
    (field: FilterDefinition) => {
      setOpen(false);
      onStartFilter?.(field);
    },
    [onStartFilter],
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      className={styles.panel}
      aria-label="Saved Views"
      trigger={
        <RobotoButton
          variant="secondary"
          // Fills the glyph while a View is applied. The label alone cannot
          // carry that: a View named after its own table reads like the idle
          // "Views" button.
          className={classNames(
            styles.trigger,
            loadedView !== null && styles.triggerActive,
          )}
          icon={<Bookmark size={16} aria-hidden />}
          title={loadedView?.name}
          // Explicit: the idle label is JSX, and the count badge must not
          // become part of the accessible name.
          aria-label={loadedView?.name ?? "Views"}
        >
          {loadedView === null ? (
            <>
              Views
              {viewCount > 0 && (
                <span className={styles.triggerCount} aria-hidden>
                  {viewCount}
                </span>
              )}
            </>
          ) : (
            loadedView.name
          )}
        </RobotoButton>
      }
    >
      {/* Never disabled: with ad-hoc filters applied this still drops them, and
          a dead row that swallows the click explains nothing. */}
      <button
        type="button"
        className={styles.resetRow}
        onClick={handleClear}
        aria-current={loadedView === null ? "true" : undefined}
      >
        {/* No leading glyph: the View rows below have none, and one centered
            against a two-line block floats between the lines. */}
        <span className={styles.rowName}>
          {loadedView === null && <LoadedMarker />}
          {resetCopy.title}
          {/* With no default set, the unfiltered table is where the customer
              lands, so this row says so. Held back until the stored default
              has been read, or the badge would flash here and then move. */}
          {!defaultView.isPending && defaultView.defaultViewId === null && (
            <DefaultMarker />
          )}
        </span>
        <span className={styles.rowMeta}>{resetCopy.description}</span>
      </button>

      {linkError !== null && (
        <p className={styles.linkError} role="alert">
          {linkError}
        </p>
      )}

      {defaultError !== null && (
        <p className={styles.linkError} role="alert">
          {defaultError}
        </p>
      )}

      <div className={styles.tabs} role="tablist" aria-label="View visibility">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "personal"}
          className={styles.tab}
          onClick={() => setTab("personal")}
        >
          Personal <span className={styles.tabCount}>{personal.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "team"}
          className={styles.tab}
          onClick={() => setTab("team")}
        >
          Team <span className={styles.tabCount}>{teamViews.length}</span>
        </button>
      </div>

      <ViewList
        isPending={isPending}
        views={shown}
        tab={tab}
        emptyStateField={
          onStartFilter === undefined ? null : filterDefinitions?.[0] ?? null
        }
        onStartFilter={handleStartFilter}
        loadedViewId={loadedView?.view_id ?? null}
        defaultViewId={defaultView.defaultViewId}
        onToggleDefault={handleToggleDefault}
        onSelect={handleSelect}
        manage={manage}
        renamingId={renamingId}
        onRenameStart={(record) => setRenamingId(record.view_id)}
        onRenameEnd={() => setRenamingId(null)}
        // A copy is made to be worked on, so it loads like a chosen row.
        onDuplicated={handleSelect}
        onDeleteRequest={setPendingDelete}
        linkFor={linkFor}
      />
      {/* Mounted only while a delete is pending. */}
      {pendingDelete !== null && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setPendingDelete(null);
            }
          }}
          title={`Delete ${pendingDelete.name}?`}
          description="This action cannot be undone."
          confirmLabel="Delete"
          destructive
          pending={manage.isBusy}
          onConfirm={() => void handleConfirmDelete(pendingDelete)}
        />
      )}
    </Popover>
  );
}

interface ViewListProps {
  isPending: boolean;
  views: ViewRecord[];
  tab: PickerTab;
  /** The field the empty state's shortcut starts a filter on, if any. */
  emptyStateField: FilterDefinition | null;
  onStartFilter: (field: FilterDefinition) => void;
  loadedViewId: string | null;
  defaultViewId: string | null;
  onToggleDefault: (view: ViewRecord) => void;
  onSelect: (view: ViewRecord) => void;
  manage: ManageViews;
  renamingId: string | null;
  onRenameStart: (view: ViewRecord) => void;
  onRenameEnd: () => void;
  onDuplicated: (copy: ViewRecord) => void;
  onDeleteRequest: (view: ViewRecord) => void;
  linkFor: (view: ViewRecord) => string;
}

/** The panel's three states: loading, nothing saved yet, and the list. */
function ViewList({
  isPending,
  views,
  tab,
  emptyStateField,
  onStartFilter,
  loadedViewId,
  defaultViewId,
  onToggleDefault,
  onSelect,
  manage,
  renamingId,
  onRenameStart,
  onRenameEnd,
  onDuplicated,
  onDeleteRequest,
  linkFor,
}: ViewListProps) {
  if (isPending) {
    return <ViewListSkeleton />;
  }

  if (views.length === 0) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyBody}>
          <p className={styles.emptyTitle}>
            No {tab === "personal" ? "personal" : "team"} Views yet
          </p>
          <p className={styles.emptyText}>
            A View is a saved set of filters you can return to in one click.
          </p>
        </div>
        {/* One shortcut as a footer row, not a chip per field: a wall of
            fields is a second filter menu, and the real one is a click away. */}
        {emptyStateField !== null && (
          <button
            type="button"
            className={styles.emptyAction}
            onClick={() => onStartFilter(emptyStateField)}
          >
            <Plus size={14} aria-hidden />
            Add a filter to get started
          </button>
        )}
      </div>
    );
  }

  return (
    <ul className={styles.list}>
      {views.map((view) => (
        <li key={view.view_id}>
          <ViewRow
            view={view}
            isLoaded={view.view_id === loadedViewId}
            isDefault={view.view_id === defaultViewId}
            onToggleDefault={onToggleDefault}
            onSelect={onSelect}
            manage={manage}
            isRenaming={view.view_id === renamingId}
            onRenameStart={onRenameStart}
            onRenameEnd={onRenameEnd}
            onDuplicated={onDuplicated}
            onDeleteRequest={onDeleteRequest}
            linkFor={linkFor}
          />
        </li>
      ))}
    </ul>
  );
}

interface ViewRowProps {
  view: ViewRecord;
  isLoaded: boolean;
  isDefault: boolean;
  onToggleDefault: (view: ViewRecord) => void;
  onSelect: (view: ViewRecord) => void;
  manage: ManageViews;
  isRenaming: boolean;
  onRenameStart: (view: ViewRecord) => void;
  onRenameEnd: () => void;
  onDuplicated: (copy: ViewRecord) => void;
  onDeleteRequest: (view: ViewRecord) => void;
  linkFor: (view: ViewRecord) => string;
}

/**
 * The attribution line sits outside the apply button: it links to a principal,
 * and an anchor nested in a button is invalid HTML. Split, both stay operable —
 * the name applies the View, the principal opens who last touched it.
 */
function ViewRow({
  view,
  isLoaded,
  isDefault,
  onToggleDefault,
  onSelect,
  manage,
  isRenaming,
  onRenameStart,
  onRenameEnd,
  onDuplicated,
  onDeleteRequest,
  linkFor,
}: ViewRowProps) {
  const applyRef = React.useRef<HTMLButtonElement>(null);
  const wasRenamingRef = React.useRef(isRenaming);

  // When the rename input unmounts, focus falls to the popup element, which
  // then wears the browser's focus ring around the whole panel. Put it back
  // on the row instead. Keyed on the transition, not the value, so a row that
  // mounts un-renaming does not steal focus from whatever has it.
  React.useEffect(() => {
    if (wasRenamingRef.current && !isRenaming) {
      applyRef.current?.focus();
    }
    wasRenamingRef.current = isRenaming;
  }, [isRenaming]);

  return (
    <div className={styles.row} aria-current={isLoaded ? "true" : undefined}>
      {/* The menu sits beside the stacked pair, so it centers on the row. */}
      <div className={styles.rowBody}>
        {isRenaming ? (
          <RenameInput view={view} manage={manage} onDone={onRenameEnd} />
        ) : (
          <button
            ref={applyRef}
            type="button"
            className={styles.rowApply}
            onClick={() => onSelect(view)}
          >
            <span className={styles.rowName}>
              {isLoaded && <LoadedMarker />}
              {view.name}
              {isDefault && <DefaultMarker />}
            </span>
          </button>
        )}
        <div className={styles.rowMeta}>
          Updated {formatDate(view.modified)} by{" "}
          <RobotoPrincipalUnderlineLink
            principal={view.modified_by}
            className={styles.rowPrincipal}
          />
        </div>
      </div>
      {/* Wrapped to lift it above `.rowApply`'s stretched hit area. */}
      <div className={styles.rowControl}>
        <ViewRowMenu
          view={view}
          manage={manage}
          linkFor={linkFor}
          onRenameStart={onRenameStart}
          onDuplicated={onDuplicated}
          onDeleteRequest={onDeleteRequest}
          isDefault={isDefault}
          onToggleDefault={onToggleDefault}
        />
      </div>
    </div>
  );
}

/**
 * Marks the View this customer starts the table from. A word rather than a
 * glyph: a star reads as "you could star this", and the affordance for setting
 * one is the row menu. Decorative to assistive tech, as the star was — the
 * row's accessible name stays the View's name.
 */
function DefaultMarker() {
  return (
    <span className={styles.defaultMarker} aria-hidden>
      Default
    </span>
  );
}

/**
 * Marks the View currently applied. A checkmark: the triangle it replaces read
 * as "expandable". Decorative — the row carries `aria-current`.
 */
function LoadedMarker() {
  return (
    <Check
      className={styles.loadedMarker}
      size={14}
      strokeWidth={2.5}
      aria-hidden
      focusable="false"
    />
  );
}

interface RenameInputProps {
  view: ViewRecord;
  manage: ManageViews;
  onDone: () => void;
}

/**
 * Inline rename, committed on Enter or blur and abandoned on Escape. An empty
 * name reverts rather than commits — the backend rejects it, and it is a
 * destructive reading of an ambiguous gesture.
 *
 * Escape is consumed here: the enclosing popover also dismisses on it, and a
 * user backing out of a rename has not asked to lose the panel too.
 */
function RenameInput({ view, manage, onDone }: RenameInputProps) {
  const [value, setValue] = React.useState(view.name);

  const commit = () => {
    const next = value.trim();
    if (next.length > 0 && next !== view.name) {
      void manage.renameView(view, next);
    }
    onDone();
  };

  return (
    <TextInput
      className={styles.renameInput}
      value={value}
      autoFocus
      // No browser form history over a View's name.
      autoComplete="off"
      aria-label={`Rename ${view.name}`}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
        } else if (event.key === "Escape") {
          // Base UI's dismiss listens on the popup and on the document, both
          // downstream of this handler.
          event.stopPropagation();
          onDone();
        }
      }}
    />
  );
}

/** Skeleton rather than a spinner: the panel's shape is known before its contents. */
function ViewListSkeleton() {
  return (
    <div className={styles.skeletonList} aria-hidden>
      <div className={styles.skeletonBarWide} />
      <div className={styles.skeletonBarNarrow} />
      <div className={styles.skeletonBarMedium} />
    </div>
  );
}
